import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { DataCard } from '@/components/DataCard/DataCard'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { UploadFileInfo } from '@progress/kendo-react-upload'
import { CreatePromoMaterialForLandingPage } from 'api/ClientArea/CreatePromoMaterials'
import { LandingPageDto } from 'api/ClientArea/LandingPageDto'
import { PreviewPromoMaterialLandingPage } from 'api/ClientArea/PreviewPromoMaterials'
import { PromoMaterialLandingPageDto } from 'api/ClientArea/PromoMaterialDto'
import { PromoMaterialsOptionsDto } from 'api/ClientArea/PromoMaterialsOptionsDto'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { errorToast, successToast, ToastContext } from 'components/Toast/Toast'
import { calculatePageIndex, calculateSkipIndex } from 'features/DataGrid/helpers'
import { pageableSettings } from 'store/grid/grid.slice'
import { ResponseError, useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { PermissionDto } from '../../../api/Identity/Types/PermissionDto'
import { PermissionsEnum } from '../../../api/Identity/Types/PermissionsEnum'
import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { hasPermission } from '../../../utils/permissionsValidator'
import { promoMaterialColumn } from './promoMaterialColumn'
import type { GridColumn } from './promoMaterialColumn'
import { PromoMaterialEditForm } from './PromoMaterialEditForm'
import { UpdatePromoMaterialValues } from './PromoMaterialForm'
import { PromoMaterialSaveForm } from './PromoMaterialSaveForm'
import { RelatedPromoMaterialUploadTable } from './RelatedPromoMaterialUploadTable'

const useRelatedPromoMaterialsFilter = () => {
  const { landingId: landingPageId } = useLandingRoute()
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())

  const { callback, forceRefresh } = useCallbackWithForceRefresh(() => {
    if (!landingPageId) return Promise.resolve(undefined)
    return clientAreaApiClient.getPromoMaterialsByLandingPageId(landingPageId, {
      pageIndex: calculatePageIndex(paginationState.skip, paginationState.take),
      pageSize: paginationState.take,
      skipCount: tableData?.skipCount,
    })
  }, [paginationState, tableData?.skipCount])
  const { data, isLoading } = useFetchOne(callback)

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isLoading) {
        setPaginationState((page) => ({ ...page, skip: 0 }))
      }
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [filter.filters])

  useEffect(() => {
    setTableData((props) => {
      return { ...props, skipCount: tableData?.skipCount, meta: data }
    })
  }, [data?.pageIndex, data?.pageSize, data?.totalResults, tableData?.skipCount, data])

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(event.filter)
  }

  const handlePageChange = (event: GridPageChangeEvent) => {
    setPaginationState(event.page)
  }

  return {
    pageData: data,
    data: (data?.items || []) as PromoMaterialLandingPageDto[],
    tableData,
    setTableData,
    pageIndex: calculatePageIndex(paginationState.skip, paginationState.take),
    pageSize: paginationState.take ?? 10,
    isLoading,
    forceRefresh,
    filter,
    setFilter,
    pagination: paginationState,
    setPagination: setPaginationState,
    handleFilterChange,
    handlePageChange,
  }
}

const useTablePager = (props: ReturnType<typeof useRelatedPromoMaterialsFilter>) => {
  const { pageData, pagination, setPagination, tableData, setTableData } = props

  useEffect(() => {
    setTableData((state) => {
      return { ...state, skipCount: tableData?.skipCount, meta: pageData }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData?.pageIndex, pageData?.pageSize, pageData?.totalResults, tableData?.skipCount])

  const handlePageChangePager = (pagerIndex: number) => {
    setPagination((state) => {
      return { ...state, skip: calculateSkipIndex(pagerIndex, pagination.take) }
    })
  }

  const handleSizeChangePager = (take: number) => {
    setPagination((state) => {
      return { ...state, skip: 0, take }
    })
  }

  const handleSkipCountChangePager = (skipCount: boolean) => {
    setTableData((state) => {
      return { ...state, skipCount }
    })
  }

  return { handlePageChangePager, handleSizeChangePager, handleSkipCountChangePager }
}

interface FileInfo extends UploadFileInfo {
  languageId: string
  promoMaterialCategoryId: number
  promoMaterialTypeId: number
  promoMaterial: string
  inEdit: boolean
}

const usePromoMaterialMutate = (props: OuterProps) => {
  const tickmillCompanyId = props.data.tickmillCompany?.id

  const { landingId: landingPageId } = useLandingRoute()

  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const setToast = useContext(ToastContext)

  const addOne = async (data: CreatePromoMaterialForLandingPage, callback: () => void) => {
    if (landingPageId && tickmillCompanyId) {
      try {
        await clientAreaApiClient.createPromoMaterialsForLandingPage({
          landingPageId,
          promoMaterials: [data],
        })
        setToast(successToast('Promo Material added successfully.'))
        callback()
      } catch (error) {
        if (error instanceof ResponseError && error?.response?.message) {
          setToast(errorToast(error.response.message))
        }
      }
    }
  }

  const updateOne = async (data: UpdatePromoMaterialValues, callback: () => void) => {
    if (landingPageId && tickmillCompanyId) {
      try {
        const { id, ...payload } = data
        await clientAreaApiClient.updatePromoMaterialsByPromoMaterialIdForLandingPage(id, payload)
        setToast(successToast('Promo Material update successfully.'))
        callback()
      } catch (error) {
        if (error instanceof ResponseError && error?.response?.message) {
          setToast(errorToast(error.response.message))
        }
      }
    }
  }

  const addBulk = async (data: FileInfo[], callback: () => void) => {
    if (landingPageId && tickmillCompanyId) {
      try {
        const promoMaterials: CreatePromoMaterialForLandingPage[] = data
          .filter((x): x is FileInfo => !!x?.name && !!x?.languageId)
          .map((x) => {
            return {
              name: x.name,
              languageId: x.languageId,
              promoMaterial: x.promoMaterial,
            } as CreatePromoMaterialForLandingPage
          })
        await clientAreaApiClient.createPromoMaterialsForLandingPage({
          landingPageId,
          promoMaterials,
        })
        setToast(successToast('Promo Materials added successfully.'))
        callback()
      } catch (error) {
        if (error instanceof ResponseError && error?.response?.message) {
          setToast(errorToast(error.response.message))
        }
      }
    }
  }

  const validate = async (data: CreatePromoMaterialForLandingPage[]) => {
    if (landingPageId && tickmillCompanyId) {
      try {
        const result = await clientAreaApiClient.validatePromoMaterialsForLandingPage({
          landingPageId,
          promoMaterials: data,
        })
        if (result.errors) {
          const errors = result.promoMaterials.map((x) => x.error?.name).filter((name) => name)
          const [error] = errors
          if (error) {
            setToast(errorToast(error))
            throw new Error(error)
          }
        } else {
          setToast(successToast('Promo Material validated successfully.'))
          return result
        }
      } catch (error) {
        if (error instanceof ResponseError) {
          if (error?.response?.message) {
            setToast(errorToast(error.response.message))
            throw new Error(error.response.message)
          }
        }

        if (error instanceof Error) {
          setToast(errorToast(error.message))
          throw new Error(error.message)
        }
      }
    }
  }

  const remove = async (data?: PromoMaterialLandingPageDto) => {
    if (data?.id) {
      try {
        await clientAreaApiClient.deletePromoMaterialsByPromoMaterialIdForLandingPage(data.id)
        setToast(successToast('Promo Material removed successfully.'))
      } catch (error) {
        if (error instanceof ResponseError && error?.response?.message) {
          setToast(errorToast(error.response.message))
        }
      }
    }
  }

  return { addOne, addBulk, updateOne, validate, remove }
}

interface ModalProps {
  data: PromoMaterialLandingPageDto | undefined
  visible: boolean
}

const initialModalState = { data: undefined, visible: false }

const useConfirmationModal = () => {
  const [confirmationModal, setConfirmationModal] = useState<ModalProps>(initialModalState)

  const handleConfirmationModalOpen = useCallback((data?: PromoMaterialLandingPageDto) => {
    if (data) {
      setConfirmationModal({ data, visible: true })
    }
  }, [])

  const handleConfirmationModalClose = useCallback(() => {
    setConfirmationModal(initialModalState)
  }, [])

  return { confirmationModal, handleConfirmationModalOpen, handleConfirmationModalClose }
}

const useLandingRoute = () => {
  const params = useParams<{ id: string }>()
  const landingId = params.id

  return { landingId }
}

interface OuterProps {
  data: LandingPageDto
  promoMaterialsOptions: PromoMaterialsOptionsDto
  permissions: PermissionDto[]
}

interface ModalProps {
  data: PromoMaterialLandingPageDto | undefined
  visible: boolean
}

export const RelatedPromoMaterialTable: React.FC<OuterProps> = (props) => {
  const { data, promoMaterialsOptions, permissions } = props

  const [isManualUploadModalOpen, setManualUploadModalOpen] = useState<boolean>(false)
  const [editModalOpen, setEditModal] = useState<ModalProps>(initialModalState)
  const [isBulkModalOpen, setBulkModalOpen] = useState<boolean>(false)
  const [promoMaterialsPreview, setPromoMaterialsPreview] = useState<
    PreviewPromoMaterialLandingPage[]
  >([])
  const [auditLogData, setAuditLogData] = useState<PromoMaterialLandingPageDto | null>(null)
  const [auditLogVisible, setAuditLogVisible] = useState(false)
  const { confirmationModal, handleConfirmationModalOpen, handleConfirmationModalClose } =
    useConfirmationModal()

  const relatedPromoMaterialsFilter = useRelatedPromoMaterialsFilter()
  const tablePager = useTablePager(relatedPromoMaterialsFilter)

  const { isLoading } = relatedPromoMaterialsFilter
  const { forceRefresh } = relatedPromoMaterialsFilter

  const mutate = usePromoMaterialMutate(props)

  const handleAddUploadModalOpen = useCallback(() => {
    setManualUploadModalOpen(true)
  }, [])

  const handleBulkUploadModalOpen = useCallback(() => {
    setBulkModalOpen(true)
  }, [])

  const handleEditModalOpen = useCallback((values: PromoMaterialLandingPageDto) => {
    setEditModal({ data: values, visible: true })
  }, [])

  const handleManualUpload = async (values: CreatePromoMaterialForLandingPage) => {
    await mutate.addOne(values, () => {
      setManualUploadModalOpen(false)
      forceRefresh()
    })
  }

  const handleUploadValidation = async (
    values: CreatePromoMaterialForLandingPage[]
  ): Promise<PreviewPromoMaterialLandingPage[] | undefined> => {
    try {
      const result = await mutate.validate(values)
      if (result?.promoMaterials) {
        setPromoMaterialsPreview(result.promoMaterials)
        return result.promoMaterials || []
      }
    } catch (error) {
      if (error instanceof ResponseError) {
        if (error?.response?.message) {
          throw new Error(error.response.message)
        }
      }

      if (error instanceof Error) {
        throw new Error(error.message)
      }
    }
  }

  const handleManualEdit = async (values: UpdatePromoMaterialValues) => {
    await mutate.updateOne(values, () => {
      setEditModal(initialModalState)
      forceRefresh()
    })
  }

  const handleBulkUpload = async (values: FileInfo[]) => {
    await mutate.addBulk(values, () => {
      setBulkModalOpen(false)
      forceRefresh()
    })
  }

  const handleRemovePromoMaterial = async (promoMaterial?: PromoMaterialLandingPageDto) => {
    if (promoMaterial?.id) {
      await mutate.remove(promoMaterial)
      forceRefresh()
      handleConfirmationModalClose()
    }
  }

  const handleAuditLogOpen = useCallback((promoMaterial: PromoMaterialLandingPageDto) => {
    setAuditLogData(promoMaterial)
    setAuditLogVisible(true)
  }, [])

  return (
    <React.Fragment>
      {auditLogVisible && auditLogData && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          promoMaterialId={auditLogData?.id || ''}
        />
      )}
      {confirmationModal.data && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={() => handleRemovePromoMaterial(confirmationModal.data)}
          onCancel={handleConfirmationModalClose}
          visible={confirmationModal.visible}
          renderTitle={
            <div>
              <p>Are you sure you want to delete this Promo Material?</p>
            </div>
          }
        />
      )}
      <Dialog open={isManualUploadModalOpen} onOpenChange={() => setManualUploadModalOpen(false)}>
        <PromoMaterialSaveForm
          data={data}
          promoMaterialsOptions={promoMaterialsOptions}
          promoMaterialsPreview={promoMaterialsPreview}
          onSubmit={handleManualUpload}
          onValidate={handleUploadValidation}
        />
      </Dialog>
      {editModalOpen.visible && editModalOpen.data && (
        <Dialog open={editModalOpen.visible} onOpenChange={() => setEditModal(initialModalState)}>
          <PromoMaterialEditForm
            data={data}
            promoMaterial={editModalOpen.data}
            promoMaterialsOptions={promoMaterialsOptions}
            onSubmit={handleManualEdit}
            permissions={permissions}
          />
        </Dialog>
      )}
      {isBulkModalOpen && data && (
        <Dialog open={isBulkModalOpen} onOpenChange={() => setBulkModalOpen(false)}>
          <RelatedPromoMaterialUploadTable
            data={data}
            promoMaterialsPreview={promoMaterialsPreview}
            onSubmit={handleBulkUpload}
            onPromoMaterialValidate={handleUploadValidation}
          />
        </Dialog>
      )}
      <div className='px-4  pb-4'>
        <DataCard title='Related Promo Material'>
          <div className='flex flex-wrap items-center gap-2'>
            <Button
              type='button'
              disabled={
                isLoading || !hasPermission(permissions, PermissionsEnum.CanCreatePromoMaterial)
              }
              onClick={handleBulkUploadModalOpen}
              data-test='save-button'
            >
              Bulk Upload
            </Button>
            <AddButton
              text='Manual Upload'
              disabled={
                isLoading || !hasPermission(permissions, PermissionsEnum.CanCreatePromoMaterial)
              }
              onClick={handleAddUploadModalOpen}
            />
          </div>
          <Loading isLoading={isLoading}>
            <RelatedPromoMaterialGrid
              {...props}
              tablePager={tablePager}
              relatedPromoMaterialsFilter={relatedPromoMaterialsFilter}
              onEdit={handleEditModalOpen}
              onDelete={handleConfirmationModalOpen}
              onAuditLogOpen={handleAuditLogOpen}
              setAuditLogModalOpen={setAuditLogVisible}
            />
          </Loading>
        </DataCard>
      </div>
    </React.Fragment>
  )
}

interface RelatedPromoMaterialGridProps {
  relatedPromoMaterialsFilter: ReturnType<typeof useRelatedPromoMaterialsFilter>
  tablePager: ReturnType<typeof useTablePager>
  onEdit(data: PromoMaterialLandingPageDto): void
  onDelete(data: PromoMaterialLandingPageDto): void
  onAuditLogOpen(data: PromoMaterialLandingPageDto): void
  setAuditLogModalOpen(value: boolean): void
  permissions: PermissionDto[]
}

const RelatedPromoMaterialGrid: React.FC<RelatedPromoMaterialGridProps> = (props) => {
  const { relatedPromoMaterialsFilter, tablePager, onEdit, onDelete, onAuditLogOpen, permissions } =
    props

  const {
    data: promoMaterials,
    pageData,
    filter,
    pagination,
    pageSize,
    pageIndex,
  } = relatedPromoMaterialsFilter
  const { handlePageChange, handleFilterChange } = relatedPromoMaterialsFilter
  const { tableData, setTableData } = relatedPromoMaterialsFilter
  const { handlePageChangePager, handleSizeChangePager, handleSkipCountChangePager } = tablePager

  const columns = useMemo(() => promoMaterialColumn(onEdit).filter(filterColumn), [onEdit])

  const actionCell = useCallback(
    (gridCellProps: GridCellProps) => (
      <td>
        <MoreButton
          items={[
            {
              name: 'Delete',
              onClick: () => onDelete(gridCellProps.dataItem),
              disabled: !hasPermission(permissions, PermissionsEnum.CanDeletePromoMaterial),
            },
            {
              name: 'Audit Log',
              onClick: () => onAuditLogOpen(gridCellProps.dataItem),
            },
          ]}
        />
      </td>
    ),
    [onAuditLogOpen, onDelete, permissions]
  )

  return (
    <TableStateContext.Provider value={[tableData, setTableData]}>
      <Grid
        resizable
        navigatable={false}
        dataItemKey='id'
        data={promoMaterials || []}
        filter={filter}
        take={pagination.take}
        skip={pagination.skip}
        total={pageData?.totalResults}
        onPageChange={handlePageChange}
        onFilterChange={handleFilterChange}
        pageable={pageableSettings}
        pager={() => (
          <Pager
            namespace='related-promo-material-table'
            onSkipCountChange={handleSkipCountChangePager}
            onPageChange={handlePageChangePager}
            onPageSizeChange={handleSizeChangePager}
            page={pageIndex}
            pageSize={pageSize}
            take={pagination.take || 10}
            skip={pagination.skip || 0}
          />
        )}
      >
        {columns.map((column, idx) => (
          <Column key={idx + 2} {...column} />
        ))}
        <Column title='' width={50} cell={actionCell} />
      </Grid>
    </TableStateContext.Provider>
  )
}

const filterColumn = (c: GridColumn) => {
  return c.show !== false
}
